import * as React from 'react';

import { cn } from '@heartplus/ui';

import { formatDurationSeconds } from '../../utils/blocknote-document/token-value-utils';

import {
  getReadOnlyDropdownTokenPresentation,
  parseDropdownTokenOptions,
  parseMultiSelectTokenValues,
} from './options';
import { INLINE_TOKEN_CLASS, INLINE_TOKEN_DROPDOWN_WIDTH } from './styles';

/**
 * Placeholder shown for a static-presentation token whose value is empty
 * (autofill found nothing and no default was seeded). An em dash is used —
 * not a hyphen — so it can't be mistaken for the literal "- " dash prefixes
 * the static summary rows render with.
 */
export const EMPTY_STATIC_TOKEN_PLACEHOLDER = '—';

type ReadOnlyTokenProps = {
  tokenType: string;
  value: string;
  optionsValue?: string;
  /**
   * Text rendered when the resolved value is empty. Defaults to '-' to keep
   * existing read-mode output byte-identical; static-presentation tokens pass
   * the em dash placeholder instead.
   */
  emptyFallback?: string;
  /**
   * Static-presentation tokens render plain text — no box/border, no option
   * color, no fixed dropdown width, no bold. Additive opt-in: when omitted (or
   * false), every branch renders exactly as before so non-static read mode and
   * read-mode tables stay byte-identical. The resolved display value is the
   * same in both modes — only the wrapping span differs.
   */
  plain?: boolean;
};

export const ReadOnlyToken: React.FC<ReadOnlyTokenProps> = ({
  tokenType,
  value,
  optionsValue,
  emptyFallback = '-',
  plain = false,
}) => {
  if (tokenType === 'multi_select') {
    const options = optionsValue ? parseDropdownTokenOptions(optionsValue) : [];
    const selectedValues = parseMultiSelectTokenValues(value);
    const displayValue = selectedValues
      .map((selectedValue) => {
        const option = options.find((item) => item.value === selectedValue);
        return option?.label ?? selectedValue;
      })
      .filter((item) => item.trim() !== '')
      .join(', ');

    if (plain) {
      return <PlainTokenSpan>{displayValue || emptyFallback}</PlainTokenSpan>;
    }

    return (
      <span
        className={cn(
          INLINE_TOKEN_CLASS,
          'border font-semibold',
          INLINE_TOKEN_DROPDOWN_WIDTH,
          'text-sm leading-5',
        )}
        contentEditable={false}
      >
        {displayValue || emptyFallback}
      </span>
    );
  }

  if (tokenType === 'dropdown' && optionsValue) {
    const { displayValue, optionClassName } =
      getReadOnlyDropdownTokenPresentation({
        value,
        optionsValue,
        emptyFallback,
      });

    if (plain) {
      return <PlainTokenSpan>{displayValue}</PlainTokenSpan>;
    }

    return (
      <span
        className={cn(
          INLINE_TOKEN_CLASS,
          'border',
          INLINE_TOKEN_DROPDOWN_WIDTH,
          optionClassName,
          'font-semibold',
        )}
        contentEditable={false}
      >
        {displayValue}
      </span>
    );
  }

  // A `duration` token stores canonical integer seconds; render it as compact prose
  // ("529" → "8m 49s") at this display seam — the value stays numeric on storage. The
  // shared formatter leaves a non-integer / unresolved payload unchanged, so legacy
  // unit-glued values ("529 s") still read verbatim. Byte-identical to the Go display
  // seam (notes formatDurationSeconds) and the editable-text flatten mirror.
  if (tokenType === 'duration') {
    const formatted = value?.trim() ? formatDurationSeconds(value) : emptyFallback;
    if (plain) {
      return <PlainTokenSpan>{formatted}</PlainTokenSpan>;
    }
    return (
      <span
        className="inline-flex items-center text-sm font-semibold leading-5"
        contentEditable={false}
      >
        {formatted}
      </span>
    );
  }

  const displayValue = value?.trim() ? value : emptyFallback;

  if (plain) {
    return <PlainTokenSpan>{displayValue}</PlainTokenSpan>;
  }

  return (
    <span
      className="inline-flex items-center text-sm font-semibold leading-5"
      contentEditable={false}
    >
      {displayValue}
    </span>
  );
};

/**
 * Plain inline rendering for static-presentation tokens: a bare span with no
 * box/border/color/bold, mirroring the static edit form's plain value span so
 * both surfaces (read mode and structured form) look identical.
 */
const PlainTokenSpan: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <span
    className="whitespace-pre-wrap text-sm font-normal leading-5"
    contentEditable={false}
  >
    {children}
  </span>
);
